import { DOCUMENT, inject, Injectable } from '@angular/core';
import type { OrderHistoryRecordModel } from '@libs/services/models';
import { CepStateService } from '@libs/shared/common';
import {
  CartDataLayerItem,
  CartItemModel,
  CartModel,
  CartTrackingChangeQuantityProduct,
  ProductListItemBaseModel,
  ProductTrackingModel,
  ShopErrorItemModel,
  toTrackingAvailability,
  TrackingCartEventType,
  TrackingCartType,
  TrackingCatalogSearchSuggestionModel,
  TrackingCheckoutStepType,
  TrackingProductModel,
} from '@libs/shop/models';
import { getTrackingPagePath } from '../context';
import { TrackingMapperService } from '../mapper';
import { TrackingGoogleService } from '../services';

@Injectable({ providedIn: 'root' })
export class CartTrackingService {
  private readonly document: Document = inject(DOCUMENT);
  private readonly tracking: TrackingGoogleService = inject(TrackingGoogleService);
  private readonly cepStateService: CepStateService = inject(CepStateService);
  private readonly trackingMapper: TrackingMapperService = inject(TrackingMapperService);

  /**
   * Tracks the `addToBasket` event for a re-ordered order. Availability is derived from the bulk
   * add-to-cart response `errors`: a SKU that could not be added is reported as `nicht verfügbar`,
   * all others as `verfügbar`. Must be called only after the add request resolves; on a hard backend
   * error nothing should be tracked.
   */
  public trackAddToCartOrder(order: OrderHistoryRecordModel, errors: ShopErrorItemModel[]): void {
    const failedSkus: ReadonlySet<string> = this.toFailedSkus(errors);

    const products: ProductTrackingModel[] = order.items.map((item, index) => ({
      id: item.sku,
      price: this.trackingMapper.formatSalesUnitPrice(item.productData?.prices?.salesUnit),
      quantity: item.amountOrdered,
      list: 'bestellübersicht',
      position: index + 1,
      bundleID: item.productData?.bundleType ? item.sku : '',
      bundleName: item.productData?.bundleType ? item.productData.details.name : '',
      promotionType: this.trackingMapper.mapPromotionType(item.productData?.bundleType, item.productData?.prices?.priceType),
      originalPrice: this.trackingMapper.mapOriginalPrice(item.productData?.prices?.salesUnitStrikethroughPrice),
      availability: toTrackingAvailability(!failedSkus.has(item.sku)),
    }));

    this.mapAndPushData('addToBasket', 'bulk', products);
  }

  public trackAddToCartListItems(items: ProductListItemBaseModel[], errors: ShopErrorItemModel[], trackingList: string | undefined = undefined): void {
    this.trackAddToCartProducts(this.toTrackingProducts(items), trackingList, errors);
  }

  public trackAddToCartProducts(
    products: (TrackingProductModel | TrackingCatalogSearchSuggestionModel)[],
    trackingList: string | undefined = undefined,
    errors: ShopErrorItemModel[] | undefined = undefined,
  ): void {
    this.trackCartEvent('addToBasket', products, trackingList, errors);
  }

  public trackRemoveFromCartProducts(
    products: TrackingProductModel[],
    trackingList: string | undefined = undefined,
  ): void {
    this.trackCartEvent('removeFromBasket', products, trackingList);
  }

  public trackCartQuantityChange(newQuantity: number, cartItem: CartItemModel, trackingList: string | undefined = undefined): void {
    trackingList ??= getTrackingPagePath(this.document);

    const products: CartTrackingChangeQuantityProduct[] = this.trackingMapper.mapProductToProductTracking(this.toTrackingProducts([cartItem]), trackingList).map(x => {
      delete x.position;

      return {
        ...x,
        newQuantity: newQuantity,
      };
    });

    this.tracking.trackEvent('updateProducts', { products: products });
  }

  public trackCheckoutStep(cart: CartModel | undefined, step: TrackingCheckoutStepType): void {
    if (!cart) {
      return;
    }

    const mappedItems: ProductTrackingModel[] = this.trackingMapper.mapProductToProductTracking(this.toTrackingProducts(cart.items), '').map(x => {
      delete x.list;
      delete x.position;

      return x;
    });

    this.tracking.trackEvent('checkoutStep', {
      // the new cep app does not have a shipping page -> skip step 2
      checkoutStep: step === 'cart' ? 1 : 3,
      checkoutProducts: mappedItems,
    });
  }

  public trackCheckout(cart: CartModel): void {
    const mappedItems: ProductTrackingModel[] = this.trackingMapper.mapProductToProductTracking(this.toTrackingProducts(cart.items), '').map(x => {
      delete x.list;
      delete x.position;

      return x;
    });

    this.tracking.trackEvent('productSale', {
      transactionId: cart.id,
      transactionTotal: this.trackingMapper.formatPrice(cart.totals.net),
      transactionGrossTotal: Number(this.trackingMapper.formatPrice(cart.totals.gross)),
      transactionTax: this.trackingMapper.formatPrice(cart.totals.tax),
      transactionDeposit: Number(this.trackingMapper.formatPrice(cart.totals.deposit)),
      transactionProducts: mappedItems,
    });
  }

  public mapAndPushData(
    cartEventType: TrackingCartEventType,
    trackingType: TrackingCartType,
    products: ProductTrackingModel[],
    searchTerm: string | null = null,
  ): void {
    const payload: Omit<CartDataLayerItem, 'event'> = {
      searchTerm,
      products,
    };

    if (cartEventType === 'addToBasket') {
      payload.addToBasketType = trackingType;
    } else {
      payload.removeFromBasketType = trackingType;
    }

    this.tracking.trackEvent(cartEventType, payload);
  }

  private trackCartEvent(
    event: TrackingCartEventType,
    products: (TrackingProductModel | TrackingCatalogSearchSuggestionModel)[],
    trackingList: string | undefined = undefined,
    errors: ShopErrorItemModel[] | undefined = undefined,
  ): void {
    trackingList ??= getTrackingPagePath(this.document);

    const addToBasketType: 'single' | 'bulk' = products.length <= 1 ? 'single' : 'bulk';

    const mappedProducts: ProductTrackingModel[] = this.trackingMapper.mapProductToProductTracking(products, trackingList);
    const failedSkus: ReadonlySet<string> | undefined = errors ? this.toFailedSkus(errors) : undefined;
    const trackingProducts: ProductTrackingModel[] = failedSkus
      ? mappedProducts.map(product => ({ ...product, availability: toTrackingAvailability(!failedSkus.has(product.id)) }))
      : mappedProducts;
    const searchTerm: string | null = this.cepStateService.getProductSearchTerm(trackingList.toLowerCase());

    this.mapAndPushData(event, addToBasketType, trackingProducts, searchTerm);
  }

  private toTrackingProducts(items: ProductListItemBaseModel[]): TrackingProductModel[] {
    return items.map((item, index) => ({
      ...item.productData,
      type: 'product',
      position: index,
      quantity: item.quantity,
    }));
  }

  /**
   * Builds the set of SKUs that the shop could not add to the cart, from the bulk add-to-cart
   * response errors (their `itemPointer` is the SKU).
   */
  private toFailedSkus(errors: ShopErrorItemModel[]): ReadonlySet<string> {
    return new Set(errors.map(error => error.itemPointer).filter((sku): sku is string => !!sku));
  }
}
